import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Search,
  Calendar,
  Crown,
  User,
  Settings,
  ShieldCheck,
  Wallet,
  Sparkles,
  ArrowRight,
  CheckCircle2,
  AlertTriangle,
  CreditCard,
  Clock,
  MessageSquare,
  RefreshCw,
  Bell,
  Lock,
  LogOut,
  Edit3,
  Camera,
  Save,
  Check,
  Phone,
  Mail,
  MapPin,
  ShieldAlert,
  X
} from 'lucide-react';
import { DashboardLayout } from '../components/dashboard/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { fetchCompanionServerStatus } from '../lib/paymentService';
import { PaymentHistory } from '../components/PaymentHistory';
import { auth } from '../lib/firebase';
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
} from 'firebase/auth';

export const DashboardPage: React.FC = () => {
  const { currentUser, userProfile, accountType, updateProfile, logout } = useAuth();
  const navigate = useNavigate();

  // Active User Tab
  const [activeSection, setActiveSection] = useState<
    'overview' | 'profile' | 'membership' | 'payment-status' | 'history' | 'security' | 'notifications'
  >('overview');

  // Gateway verification testing state
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationFeedback, setVerificationFeedback] = useState<string | null>(null);

  // Profile Edit form state
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [editDisplayName, setEditDisplayName] = useState(userProfile?.displayName || userProfile?.name || '');
  const [editBio, setEditBio] = useState(userProfile?.bio || '');
  const [editCity, setEditCity] = useState(userProfile?.city || '');
  const [editHourlyRate, setEditHourlyRate] = useState(userProfile?.hourlyRate || 1500);
  const [editPhone, setEditPhone] = useState(userProfile?.phone || '');
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [profileSuccessMsg, setProfileSuccessMsg] = useState<string | null>(null);

  // Security / Password form state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [securityErrorMsg, setSecurityErrorMsg] = useState<string | null>(null);
  const [securitySuccessMsg, setSecuritySuccessMsg] = useState<string | null>(null);

  // User notifications
  const [userNotifications, setUserNotifications] = useState(() => [
    {
      id: 'un-1',
      title: 'Welcome to PairUpToday',
      body: 'Your account has been initialized. Complete membership payment to activate your companion profile.',
      time: 'Recent',
      read: false,
    },
    ...(userProfile?.isIdVerified === true
      ? [
          {
            id: 'un-2',
            title: 'KYC Document Verified',
            body: 'Government ID verification was completed. You have received the verified badge.',
            time: 'Verified',
            read: true,
          },
        ]
      : []),
  ]);

  const isCompanion = accountType === 'companion';
  const isPaymentVerified = userProfile?.payment_verified === true;
  const isPaymentSuccess = userProfile?.payment_status === 'Success';
  const isProfileActive =
    userProfile?.companion_profile_status === 'Active' && isPaymentVerified && isPaymentSuccess;

  // Authoritative Gateway Verification Status Check
  const handleVerifyGateway = async () => {
    setIsVerifying(true);
    setVerificationFeedback(null);
    try {
      const companionId = auth.currentUser?.uid || userProfile?.uid || '';
      if (!companionId) {
        setVerificationFeedback('Please sign in with your authenticated companion account first.');
        return;
      }
      const serverStatus = await fetchCompanionServerStatus(companionId);

      if (
        serverStatus &&
        serverStatus.companion_profile_status === 'Active' &&
        serverStatus.payment_status === 'Success' &&
        serverStatus.payment_verified === true
      ) {
        await updateProfile({
          companion_profile_status: 'Active',
          membership_status: 'ACTIVE',
          payment_status: 'Success',
          payment_verified: true,
        });
        setVerificationFeedback('Payment verified by Gateway! Profile is now ACTIVE.');
      } else {
        setVerificationFeedback(
          'No verified payment found on server. Please complete checkout on the Membership page to activate your profile.'
        );
      }
    } catch (err: any) {
      setVerificationFeedback(err.message || 'Verification network error');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSavingProfile(true);
    setProfileSuccessMsg(null);
    try {
      await updateProfile({
        displayName: editDisplayName,
        name: editDisplayName,
        bio: editBio,
        city: editCity,
        hourlyRate: Number(editHourlyRate),
        phone: editPhone,
      });
      setProfileSuccessMsg('Profile updated successfully!');
      setIsEditingProfile(false);
      setTimeout(() => setProfileSuccessMsg(null), 3000);
    } catch {
      setProfileSuccessMsg('Error updating profile');
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setSecurityErrorMsg(null);
    setSecuritySuccessMsg(null);

    if (!currentPassword.trim()) {
      setSecurityErrorMsg('Please enter your current password.');
      return;
    }
    if (!newPassword || newPassword.length < 8) {
      setSecurityErrorMsg('New password must be at least 8 characters long.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setSecurityErrorMsg('Passwords do not match.');
      return;
    }

    const user = auth.currentUser;
    if (!user || !user.email) {
      setSecurityErrorMsg('You must be signed in with an email and password account to change your password.');
      return;
    }

    setIsUpdatingPassword(true);
    try {
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);
      setSecuritySuccessMsg('Security credentials updated in Firebase Authentication.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => setSecuritySuccessMsg(null), 3500);
    } catch (err: any) {
      const code = err?.code || '';
      if (code === 'auth/wrong-password' || code === 'auth/invalid-credential') {
        setSecurityErrorMsg('Current password is incorrect.');
      } else if (code === 'auth/requires-recent-login') {
        setSecurityErrorMsg('Please sign out and sign in again before changing your password.');
      } else {
        setSecurityErrorMsg(err?.message || 'Failed to update password.');
      }
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  const handleSignOut = () => {
    logout();
    navigate('/');
  };

  const displayName = userProfile?.displayName || userProfile?.name || currentUser || 'Member';
  const legalName = userProfile?.legalName || userProfile?.name || 'Verified Member';

  return (
    <DashboardLayout
      title="User Panel Dashboard"
      subtitle={`Welcome, ${displayName}! Your account data and membership management.`}
    >
      <div className="space-y-6">
        {/* Navigation Tabs Bar for all 9 User Panel Requirements */}
        <div className="bg-white p-2 rounded-2xl border border-slate-200 shadow-2xs overflow-x-auto scrollbar-none flex items-center gap-1">
          {[
            { id: 'overview', label: 'Overview', icon: User },
            { id: 'payment-status', label: 'Payment Status & Rule', icon: CreditCard },
            { id: 'history', label: 'Payment History', icon: Calendar },
            { id: 'membership', label: 'Membership Plan', icon: Crown },
            { id: 'profile', label: 'Edit Profile', icon: Edit3 },
            { id: 'security', label: 'Security & Password', icon: Lock },
            { id: 'notifications', label: 'Notifications', icon: Bell, badge: userNotifications.filter((n) => !n.read).length || undefined },
          ].map((tab) => {
            const Icon = tab.icon;
            const isActive = activeSection === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveSection(tab.id as any)}
                className={`px-3 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 shrink-0 ${
                  isActive
                    ? 'bg-pink-600 text-white shadow-xs'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                <span>{tab.label}</span>
                {tab.badge && (
                  <span className="px-1.5 py-0.2 rounded-full bg-pink-500 text-white text-[9px] font-mono font-bold">
                    {tab.badge}
                  </span>
                )}
              </button>
            );
          })}

          <button
            type="button"
            onClick={handleSignOut}
            className="ml-auto px-3 py-2 bg-slate-100 hover:bg-rose-50 text-slate-700 hover:text-rose-600 rounded-xl text-xs font-bold transition-colors cursor-pointer flex items-center gap-1.5 shrink-0"
            title="Sign out of your account"
          >
            <LogOut className="w-3.5 h-3.5" />
            <span>Sign Out</span>
          </button>
        </div>

        {/* ========================================================= */}
        {/* SECTION: OVERVIEW */}
        {/* ========================================================= */}
        {activeSection === 'overview' && (
          <div className="space-y-6 animate-in fade-in duration-150">
            {/* Profile Identity & Legal Name Privacy Card */}
            <div className="p-5 sm:p-6 bg-white rounded-3xl border border-slate-200 shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-start sm:items-center gap-4">
                <img
                  src={
                    userProfile?.avatarUrl ||
                    'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&w=150&q=80'
                  }
                  alt={displayName}
                  className="w-16 h-16 rounded-2xl object-cover border-2 border-pink-200 shrink-0"
                />
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-xl font-black text-slate-900">{displayName}</h2>
                    {userProfile?.isIdVerified === true ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                        Government ID Verified
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-50 text-amber-700 border border-amber-200">
                        <Clock className="w-3.5 h-3.5 text-amber-600" />
                        KYC Verification Pending
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Public Name: <strong className="text-slate-800">{displayName}</strong> • Legal Name ({legalName}) is protected by KYC encryption.
                  </p>
                  <p className="text-[11px] text-slate-400 font-mono mt-0.5">
                    Account: {userProfile?.email || 'Authenticated User'} • Role: {isCompanion ? 'Companion Host' : 'Client'}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => navigate('/kyc')}
                  className="px-4 py-2 bg-pink-50 hover:bg-pink-100 text-pink-700 text-xs font-bold rounded-xl transition-colors inline-flex items-center gap-1.5 cursor-pointer border border-pink-200"
                >
                  <ShieldCheck className="w-3.5 h-3.5 text-pink-600" />
                  <span>KYC ID Center</span>
                </button>
                <button
                  type="button"
                  onClick={() => setActiveSection('profile')}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-xl transition-colors inline-flex items-center gap-1.5 cursor-pointer"
                >
                  <Edit3 className="w-3.5 h-3.5" />
                  <span>Edit Profile</span>
                </button>
              </div>
            </div>

            {/* Companion Inactive/Active Authoritative Banner */}
            {isCompanion && (
              <div
                className={`p-5 rounded-3xl border transition-all ${
                  isProfileActive
                    ? 'bg-emerald-50/70 border-emerald-200 text-emerald-950'
                    : 'bg-rose-50/80 border-rose-300 text-rose-950 shadow-xs'
                }`}
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span
                        className={`w-3 h-3 rounded-full ${isProfileActive ? 'bg-emerald-500' : 'bg-rose-500 animate-pulse'}`}
                      />
                      <h3 className="text-sm font-black uppercase tracking-wide">
                        {isProfileActive
                          ? '🟢 Account Status: ACTIVE (Payment Verified)'
                          : '🔴 Account Status: RESTRICTED / INACTIVE (Payment Required)'}
                      </h3>
                    </div>
                    <p className="text-xs opacity-90 leading-relaxed">
                      {isProfileActive
                        ? 'Your companion profile is active! You are visible in search listings and ready to receive client booking requests.'
                        : 'Rule: Companion payment verified না হলে account Active হবে না। Payment successful হলে membership/account automatically activate হবে।'}
                    </p>
                    <div className="text-[11px] font-mono opacity-80 pt-1">
                      Current Status: {userProfile?.companion_profile_status || 'Inactive'} • Gateway Verified: {isPaymentVerified ? 'YES' : 'NO'}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    {!isProfileActive ? (
                      <>
                        <button
                          type="button"
                          disabled={isVerifying}
                          onClick={handleVerifyGateway}
                          className="px-4 py-2.5 bg-purple-600 hover:bg-purple-700 text-white font-bold text-xs rounded-xl shadow-xs transition-all cursor-pointer flex items-center gap-1.5 disabled:opacity-50"
                        >
                          <CreditCard className="w-4 h-4" />
                          <span>{isVerifying ? 'Verifying...' : 'Verify Gateway Now'}</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setActiveSection('membership')}
                          className="px-4 py-2.5 bg-white hover:bg-slate-100 text-slate-800 border border-slate-300 font-bold text-xs rounded-xl transition-all cursor-pointer"
                        >
                          View Plans
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setActiveSection('membership')}
                        className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-xl transition-colors cursor-pointer"
                      >
                        Manage Membership
                      </button>
                    )}
                  </div>
                </div>

                {verificationFeedback && (
                  <div className="mt-3 p-3 rounded-xl bg-white/90 text-xs font-bold text-slate-800 border border-black/10">
                    Server Message: {verificationFeedback}
                  </div>
                )}
              </div>
            )}

            {/* Quick Actions Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div
                onClick={() => setActiveSection('membership')}
                className="p-5 rounded-3xl bg-white border border-slate-200 hover:border-amber-300 hover:shadow-md transition-all cursor-pointer group flex flex-col justify-between"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 rounded-2xl bg-amber-100 text-amber-700 flex items-center justify-center group-hover:scale-105 transition-transform">
                    <Crown className="w-6 h-6" />
                  </div>
                  <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-amber-600 group-hover:translate-x-1 transition-all" />
                </div>
                <div>
                  <strong className="text-base font-bold text-slate-900 block">Membership Status</strong>
                  <p className="text-xs text-slate-500 mt-1">
                    Current tier: <strong>{userProfile?.membership?.plan || 'STARTER'}</strong> ({isProfileActive ? 'ACTIVE' : (userProfile?.membership?.status || 'INACTIVE')})
                  </p>
                </div>
              </div>

              <div
                onClick={() => setActiveSection('history')}
                className="p-5 rounded-3xl bg-white border border-slate-200 hover:border-purple-300 hover:shadow-md transition-all cursor-pointer group flex flex-col justify-between"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 rounded-2xl bg-purple-100 text-purple-600 flex items-center justify-center group-hover:scale-105 transition-transform">
                    <Calendar className="w-6 h-6" />
                  </div>
                  <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-purple-600 group-hover:translate-x-1 transition-all" />
                </div>
                <div>
                  <strong className="text-base font-bold text-slate-900 block">Payment History</strong>
                  <p className="text-xs text-slate-500 mt-1">
                    View receipts, successful / pending transactions, and download invoices.
                  </p>
                </div>
              </div>

              <div
                onClick={() => setActiveSection('profile')}
                className="p-5 rounded-3xl bg-white border border-slate-200 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer group flex flex-col justify-between"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 rounded-2xl bg-blue-100 text-blue-600 flex items-center justify-center group-hover:scale-105 transition-transform">
                    <User className="w-6 h-6" />
                  </div>
                  <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-all" />
                </div>
                <div>
                  <strong className="text-base font-bold text-slate-900 block">Edit Profile</strong>
                  <p className="text-xs text-slate-500 mt-1">
                    Update your public bio, location, rates, and contact details.
                  </p>
                </div>
              </div>
            </div>

            {/* User-Scoped Transaction History View */}
            <div className="pt-2">
              <PaymentHistory
                viewMode="user"
                title="My Payment History"
                subtitle="Your personal membership payments and verified transactions."
              />
            </div>
          </div>
        )}

        {/* ========================================================= */}
        {/* SECTION: PAYMENT STATUS & RULES */}
        {/* ========================================================= */}
        {activeSection === 'payment-status' && (
          <div className="space-y-6 animate-in fade-in duration-150">
            <div>
              <h2 className="text-xl font-black text-slate-900">Companion Payment Status & Rules</h2>
              <p className="text-xs text-slate-500">
                Authoritative platform requirements for companion account activation.
              </p>
            </div>

            <div className="p-6 bg-white rounded-3xl border border-slate-200 shadow-xs space-y-4">
              <div className="flex items-center gap-3">
                <div
                  className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
                    isProfileActive ? 'bg-emerald-100 text-emerald-600' : 'bg-rose-100 text-rose-600'
                  }`}
                >
                  <CreditCard className="w-6 h-6" />
                </div>
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-400 font-mono">
                    Current Account Status
                  </div>
                  <div className="text-xl font-black text-slate-900">
                    {isProfileActive ? 'ACTIVE & VERIFIED' : 'RESTRICTED / INACTIVE'}
                  </div>
                </div>
              </div>

              <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 text-xs space-y-2 text-slate-700">
                <strong className="text-slate-900 block font-bold">Rule Enforcement Summary:</strong>
                <ul className="space-y-1.5 list-disc list-inside leading-relaxed text-slate-600">
                  <li>
                    <strong>Companion payment verified না হলে account Active হবে না:</strong> You cannot receive booking requests or appear in public browse listings until your membership payment is confirmed by Razorpay.
                  </li>
                  <li>
                    <strong>Payment successful হলে membership/account automatically activate হবে:</strong> Once payment succeeds and passes HMAC cryptographic verification, your profile switches to Active instantly.
                  </li>
                  <li>
                    <strong>Failed / Pending হলে restricted status থাকবে:</strong> If your transaction is under banking review or rejected, your profile remains restricted.
                  </li>
                </ul>
              </div>

              <div className="pt-2 flex items-center gap-3">
                <button
                  type="button"
                  disabled={isVerifying}
                  onClick={handleVerifyGateway}
                  className="px-5 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl text-xs font-bold transition-all shadow-xs cursor-pointer flex items-center gap-2"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${isVerifying ? 'animate-spin' : ''}`} />
                  <span>Verify Payment Status Now</span>
                </button>

                <Link
                  to="/membership"
                  className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-800 rounded-xl text-xs font-bold transition-colors"
                >
                  Go to Membership Checkout
                </Link>
              </div>

              {verificationFeedback && (
                <div className="p-3 bg-purple-50 border border-purple-200 rounded-xl text-xs font-bold text-purple-900">
                  {verificationFeedback}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ========================================================= */}
        {/* SECTION: PAYMENT HISTORY */}
        {/* ========================================================= */}
        {activeSection === 'history' && (
          <div className="space-y-4 animate-in fade-in duration-150">
            <PaymentHistory
              viewMode="user"
              title="Personal Payment Ledger"
              subtitle="All transactions associated with your personal account."
            />
          </div>
        )}

        {/* ========================================================= */}
        {/* SECTION: MEMBERSHIP PLAN */}
        {/* ========================================================= */}
        {activeSection === 'membership' && (
          <div className="space-y-6 animate-in fade-in duration-150">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-black text-slate-900">Membership Plan</h2>
                <p className="text-xs text-slate-500">
                  Review your active subscription, upgrade tiers, and view perks.
                </p>
              </div>
              <Link
                to="/membership"
                className="px-4 py-2 bg-gradient-to-r from-pink-500 to-rose-600 text-white rounded-xl text-xs font-bold shadow-xs hover:from-pink-600 hover:to-rose-700 transition-all"
              >
                Change or Upgrade Plan →
              </Link>
            </div>

            <div className="p-6 bg-white rounded-3xl border border-slate-200 shadow-xs space-y-4">
              <div className="flex items-center justify-between pb-4 border-b border-slate-100">
                <div>
                  <span className="text-xs font-bold uppercase text-slate-400 font-mono">Current Plan</span>
                  <div className="text-2xl font-black text-slate-900">
                    {userProfile?.membership?.plan || 'STARTER'}
                  </div>
                </div>
                <div className="text-right">
                  <span className="text-xs font-bold uppercase text-slate-400 font-mono">Status</span>
                  <div>
                    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold ${isProfileActive ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                      {isProfileActive ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />}
                      {isProfileActive ? 'ACTIVE' : (userProfile?.membership?.status || 'INACTIVE')}
                    </span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                <div className="p-3 rounded-2xl bg-slate-50 border border-slate-200">
                  <span className="text-slate-400 block font-mono text-[10px] uppercase">Plan Price</span>
                  <strong className="text-slate-900 text-base font-black">
                    ₹{userProfile?.membership?.price || 449}/month
                  </strong>
                </div>

                <div className="p-3 rounded-2xl bg-slate-50 border border-slate-200">
                  <span className="text-slate-400 block font-mono text-[10px] uppercase">Next Billing</span>
                  <strong className="text-slate-900 text-base font-black">
                    {userProfile?.membership?.nextBillingDate || '30 days from activation'}
                  </strong>
                </div>

                <div className="p-3 rounded-2xl bg-slate-50 border border-slate-200">
                  <span className="text-slate-400 block font-mono text-[10px] uppercase">Platform Commission</span>
                  <strong className="text-emerald-700 text-base font-black">
                    10% Flat Rate
                  </strong>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ========================================================= */}
        {/* SECTION: PROFILE EDIT */}
        {/* ========================================================= */}
        {activeSection === 'profile' && (
          <div className="space-y-6 animate-in fade-in duration-150 max-w-2xl">
            <div>
              <h2 className="text-xl font-black text-slate-900">Edit Personal Information</h2>
              <p className="text-xs text-slate-500">
                Update your public profile, rates, city location, and bio.
              </p>
            </div>

            {profileSuccessMsg && (
              <div className="p-3 rounded-2xl bg-emerald-50 border border-emerald-200 text-xs font-bold text-emerald-800 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                <span>{profileSuccessMsg}</span>
              </div>
            )}

            <form onSubmit={handleSaveProfile} className="p-6 bg-white rounded-3xl border border-slate-200 shadow-xs space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Public Display Name
                </label>
                <input
                  type="text"
                  required
                  value={editDisplayName}
                  onChange={(e) => setEditDisplayName(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-pink-500"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  City Location
                </label>
                <input
                  type="text"
                  value={editCity}
                  onChange={(e) => setEditCity(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-pink-500"
                />
              </div>

              {isCompanion && (
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Hourly Booking Rate (₹ INR)
                  </label>
                  <input
                    type="number"
                    value={editHourlyRate}
                    onChange={(e) => setEditHourlyRate(Number(e.target.value))}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-pink-500"
                  />
                </div>
              )}

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Phone Number
                </label>
                <input
                  type="text"
                  value={editPhone}
                  onChange={(e) => setEditPhone(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-pink-500"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  About Me / Bio
                </label>
                <textarea
                  rows={3}
                  value={editBio}
                  onChange={(e) => setEditBio(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-pink-500"
                />
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={isSavingProfile}
                  className="px-5 py-2.5 bg-gradient-to-r from-pink-500 to-rose-600 text-white rounded-xl text-xs font-bold shadow-xs hover:from-pink-600 hover:to-rose-700 transition-all cursor-pointer flex items-center gap-1.5"
                >
                  <Save className="w-3.5 h-3.5" />
                  <span>{isSavingProfile ? 'Saving...' : 'Save Profile Changes'}</span>
                </button>
              </div>
            </form>
          </div>
        )}

        {/* ========================================================= */}
        {/* SECTION: PASSWORD / SECURITY */}
        {/* ========================================================= */}
        {activeSection === 'security' && (
          <div className="space-y-6 animate-in fade-in duration-150 max-w-2xl">
            <div>
              <h2 className="text-xl font-black text-slate-900">Security & Password Settings</h2>
              <p className="text-xs text-slate-500">
                Update account credentials and review security authentication status.
              </p>
            </div>

            {securityErrorMsg && (
              <div className="p-3 rounded-2xl bg-rose-50 border border-rose-200 text-xs font-bold text-rose-800 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0" />
                <span>{securityErrorMsg}</span>
              </div>
            )}

            {securitySuccessMsg && (
              <div className="p-3 rounded-2xl bg-emerald-50 border border-emerald-200 text-xs font-bold text-emerald-800 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                <span>{securitySuccessMsg}</span>
              </div>
            )}

            <form onSubmit={handleUpdatePassword} className="p-6 bg-white rounded-3xl border border-slate-200 shadow-xs space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Current Password
                </label>
                <input
                  type="password"
                  required
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-pink-500"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  New Password
                </label>
                <input
                  type="password"
                  required
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-pink-500"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-pink-500"
                />
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={isUpdatingPassword}
                  className="px-5 py-2.5 bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white rounded-xl text-xs font-bold transition-all cursor-pointer"
                >
                  {isUpdatingPassword ? 'Updating Password...' : 'Update Password'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* ========================================================= */}
        {/* SECTION: NOTIFICATIONS */}
        {/* ========================================================= */}
        {activeSection === 'notifications' && (
          <div className="space-y-4 animate-in fade-in duration-150 max-w-2xl">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-black text-slate-900">Notifications</h2>
                <p className="text-xs text-slate-500">
                  Account alerts, payment reminders, and booking notifications.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setUserNotifications((prev) => prev.map((n) => ({ ...n, read: true })))}
                className="text-xs font-bold text-pink-600 hover:text-pink-700 cursor-pointer"
              >
                Mark all read
              </button>
            </div>

            <div className="space-y-3">
              {userNotifications.map((n) => (
                <div
                  key={n.id}
                  className={`p-4 rounded-2xl border transition-all flex items-start gap-3 ${
                    n.read ? 'bg-white border-slate-200 text-slate-700' : 'bg-pink-50/60 border-pink-200 text-slate-900 shadow-2xs'
                  }`}
                >
                  <div className="p-2 rounded-xl bg-pink-100 text-pink-600 shrink-0 mt-0.5">
                    <Bell className="w-4 h-4" />
                  </div>
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center justify-between">
                      <strong className="text-xs font-bold text-slate-900">{n.title}</strong>
                      <span className="text-[10px] text-slate-400 font-mono">{n.time}</span>
                    </div>
                    <p className="text-xs text-slate-500 leading-relaxed">{n.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};
